import React from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Buyer delivery-centre master (mig 164). A DC belongs to a buyer and (normally) a brand.
// transit_days / grc_days_typical are optional — the values arrive from Rajan's DC sheet.
export default function DeliveryCentres() {
  const { user } = useAuth();
  const canManage = ['admin', 'management'].includes(user?.role);
  const [rows, setRows] = React.useState([]);
  const [buyers, setBuyers] = React.useState([]);
  const [brands, setBrands] = React.useState([]);
  const [form, setForm] = React.useState(null);   // create/edit form, or null
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    api.get('/logistics/delivery-centres').then(r => setRows(r.data.data || [])).catch(() => {});
  }, []);
  React.useEffect(() => {
    load();
    api.get('/buyers').then(r => setBuyers((r.data.data || []).filter(b => b.deleted_at == null))).catch(() => {});
    api.get('/buyers/brands').then(r => setBrands(r.data.data || [])).catch(() => setBrands([]));
  }, [load]);

  const brandsFor = (buyerId) => brands.filter(b => b.buyer_id === buyerId);
  const blank = { buyer_id: '', brand_id: '', dc_code: '', dc_name: '', city: '', state: '', pincode: '', gstin: '', contact_name: '', contact_phone: '', contact_email: '', transit_days: '', grc_days_typical: '' };
  const openNew = () => { setErr(''); setForm({ ...blank, _new: true }); };
  const openEdit = (r) => { setErr(''); setForm({ ...r, transit_days: r.transit_days ?? '', grc_days_typical: r.grc_days_typical ?? '', _new: false }); };

  const save = async () => {
    const f = form;
    if (!f.buyer_id || !f.dc_code || !f.dc_name) { setErr('Buyer, DC code and DC name are required'); return; }
    setBusy(true); setErr('');
    const body = { ...f }; delete body._new;
    try {
      if (f._new) await api.post('/logistics/delivery-centres', body);
      else await api.put(`/logistics/delivery-centres/${f.id}`, body);
      setForm(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete DC ${r.dc_name}?`)) return;
    try { await api.delete(`/logistics/delivery-centres/${r.id}`); load(); } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11 };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: '#666' }}>Delivery centres per buyer/brand. Transit &amp; GRC days are optional until the DC sheet lands.</div>
        {canManage && <button onClick={openNew} style={{ padding: '7px 14px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add DC</button>}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Buyer', 'Brand', 'Code', 'Name', 'City', 'Contact email', 'Transit d', 'GRC d', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#aaa' }}>No delivery centres yet.</td></tr>}
          {rows.map(r => (
            <tr key={r.id}>
              <td style={td}>{r.buyer_name || '—'}</td>
              <td style={td}>{r.brand_name || <span style={{ color: '#bbb' }}>—</span>}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>{r.dc_code}</td>
              <td style={td}>{r.dc_name}</td>
              <td style={td}>{r.city || '—'}</td>
              <td style={td}>{r.contact_email || <span style={{ color: '#bbb' }}>—</span>}</td>
              <td style={td}>{r.transit_days ?? <span style={{ color: '#bbb' }}>—</span>}</td>
              <td style={td}>{r.grc_days_typical ?? <span style={{ color: '#bbb' }}>—</span>}</td>
              <td style={td}>{canManage && (<>
                <button onClick={() => openEdit(r)} style={{ padding: '4px 10px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Edit</button>
                <button onClick={() => remove(r)} style={{ padding: '4px 10px', background: '#FDECEC', border: '1px solid #F3C7C7', color: '#A32D2D', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Delete</button>
              </>)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {form && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setForm(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 22, width: 460, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{form._new ? 'Add' : 'Edit'} delivery centre</div>
            {err && <div style={{ background: '#FDECEC', color: '#A32D2D', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ gridColumn: '1 / 3' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>Buyer *</label>
                <select value={form.buyer_id} disabled={!form._new} onChange={e => setForm(f => ({ ...f, buyer_id: e.target.value, brand_id: '' }))} style={inp}>
                  <option value="">— buyer —</option>
                  {buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / 3' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>Brand (belongs to the buyer)</label>
                <select value={form.brand_id || ''} onChange={e => setForm(f => ({ ...f, brand_id: e.target.value }))} style={inp}>
                  <option value="">— none —</option>
                  {brandsFor(form.buyer_id).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              {[['dc_code', 'DC code *'], ['dc_name', 'DC name *'], ['city', 'City'], ['state', 'State'], ['pincode', 'Pincode'], ['gstin', 'GSTIN'], ['contact_name', 'Contact'], ['contact_phone', 'Phone'], ['contact_email', 'Contact email'], ['transit_days', 'Transit days'], ['grc_days_typical', 'GRC days']].map(([k, label]) => (
                <div key={k}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>{label}</label>
                  <input value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={inp} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={() => setForm(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button disabled={busy} onClick={save} style={{ padding: '8px 18px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
